import React, { FC, useEffect, useState } from "react";

import { DropdownChangeParams } from "primereact/dropdown";
import { InputNumberValueChangeParams } from "primereact/inputnumber";
import { InputSwitchChangeParams } from "primereact/inputswitch";
import { DashboardEditorProps } from "components/ComponentType";
import SparkLineAdvancedInputs from "../sparkline-advanced/SparkLineAdvancedInputs";
import SparkLineInputs from "./SparkLineInputs";
import { SparkLineProps } from "./SparkLineTypes";

const defaultSparkLineConfig: SparkLineProps = {
  valueScaling: 1,
  decimalPlaces: 0,
  labelFontSize: "2rem",
  sparkLineWidth: 300,
  dataset: null,
  count: 14,
  unit: "",
  showMinMax: false,
  showCurrent: false,
  overlayLabel: false,
  vertical: "center",
  horizontal: "center",
  updateFrequency: 0,
  lineColor: "#41c3f9",
  lineWidth: 2,
};

const defaultAdvancedSparkLineConfig: Partial<SparkLineProps> = {
  lineColor: "#5C7577",
  baselineColor: "#ABBCBE",
  fillAboveColor: "#DAE2E3",
  fillBelowColor: "#CED8D9",
  baselineWidth: 2,
}

interface SparkLineEditorProps extends DashboardEditorProps<SparkLineProps> {
  advanced?: boolean;
}

const SparkLineEditor: FC<SparkLineEditorProps> = ({ config, onConfigChange, advanced }) => {
  const [currentConfig, setCurrentConfig] = useState(() => {
    if (Object.keys(config).length === 0) {
      return advanced
        ? { ...defaultSparkLineConfig, ...defaultAdvancedSparkLineConfig }
        : defaultSparkLineConfig;
    }
    return config;
  });

  useEffect(() => {
    onConfigChange(currentConfig);
  }, [currentConfig]);

  const setConfigPropertyState = (
    property: keyof SparkLineProps,
    value: string | number | boolean | null
  ) => {
    setCurrentConfig(prevState => ({ ...prevState, [property]: value }));
  };

  const handleConfigChange = (
    e:
      | InputNumberValueChangeParams
      | React.ChangeEvent<HTMLInputElement>
      | InputSwitchChangeParams
      | DropdownChangeParams
  ): void => {
    setConfigPropertyState(e.target.id as keyof SparkLineProps, e.target.value);
  };

  return (
    <div className="flex flex-wrap gap-2">
      <SparkLineInputs
        currentConfig={currentConfig}
        onDatasetChange={setConfigPropertyState}
        onConfigChange={handleConfigChange}
      />
      {advanced && (
        <SparkLineAdvancedInputs
          currentConfig={currentConfig}
          onDatasetChange={setConfigPropertyState}
          onConfigChange={handleConfigChange}
        />
      )}
    </div>
  );
};

export default SparkLineEditor;
